import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { downloadDocument, fmtErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDateTime } from "@/components/badges";
import { CheckCircle2, Columns2, Download, Loader2, MessageSquarePlus, Undo2, Upload } from "lucide-react";

function PreviewPane({ docId, pins, onImageClick, onPinClick, activePinId, draft, testPrefix }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    let u;
    setUrl(null);
    setFailed(false);
    if (!docId) return;
    api
      .get(`/documents/${docId}/preview`, { responseType: "blob" })
      .then((r) => {
        u = URL.createObjectURL(r.data);
        setUrl(u);
      })
      .catch(() => setFailed(true));
    return () => {
      if (u) URL.revokeObjectURL(u);
    };
  }, [docId]);

  const handleClick = (e) => {
    if (!onImageClick || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    if (x < 0 || x > 100 || y < 0 || y > 100) return;
    onImageClick({ x, y });
  };

  if (!docId) return null;
  if (failed)
    return (
      <div className="flex h-64 items-center justify-center rounded-md border border-dashed text-sm text-slate-500" data-testid={`${testPrefix}-preview-unsupported`}>
        Preview not available for this file type. Use Download instead.
      </div>
    );
  if (!url)
    return (
      <div className="flex h-64 items-center justify-center" data-testid={`${testPrefix}-preview-loading`}>
        <Loader2 className="h-6 w-6 animate-spin text-sky-600" />
      </div>
    );

  return (
    <div className="relative inline-block max-w-full cursor-crosshair" ref={ref} onClick={handleClick} data-testid={`${testPrefix}-canvas`}>
      <img src={url} alt="document preview" className="max-h-[60vh] w-auto max-w-full rounded-md border" />
      {(pins || []).map((p, idx) => (
        <button
          key={p.id}
          onClick={(e) => {
            e.stopPropagation();
            onPinClick && onPinClick(p.id);
          }}
          className={`absolute flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 font-mono text-[10px] font-bold shadow transition-transform hover:scale-125 ${
            p.status === "resolved"
              ? "border-emerald-700 bg-emerald-500 text-white"
              : activePinId === p.id
                ? "scale-125 border-slate-900 bg-amber-500 text-slate-900"
                : "border-white bg-amber-500 text-slate-900"
          }`}
          style={{ left: `${p.x}%`, top: `${p.y}%` }}
          title={p.text}
          data-testid={`annotation-pin-${p.id}`}
        >
          {idx + 1}
        </button>
      ))}
      {draft && (
        <span
          className="absolute flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-dashed border-slate-900 bg-white/80"
          style={{ left: `${draft.x}%`, top: `${draft.y}%` }}
          data-testid="annotation-draft-pin"
        >
          <MessageSquarePlus className="h-3.5 w-3.5 text-slate-900" />
        </span>
      )}
    </div>
  );
}

function VersionSelect({ value, versions, onChange, testId }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-full max-w-xs font-mono text-xs" data-testid={testId}>
        <SelectValue placeholder="Select revision" />
      </SelectTrigger>
      <SelectContent>
        {versions.map((v) => (
          <SelectItem key={v.id} value={v.id} data-testid={`${testId}-option-${v.id}`}>
            {v.version} · {v.uploaded_by_email} · {fmtDateTime(v.uploaded_at)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function DocumentViewer({ doc, open, onOpenChange }) {
  const qc = useQueryClient();
  const [compare, setCompare] = useState(false);
  const [leftId, setLeftId] = useState(null);
  const [rightId, setRightId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [draftText, setDraftText] = useState("");
  const [activePinId, setActivePinId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [verFile, setVerFile] = useState(null);
  const [verLabel, setVerLabel] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open && doc) {
      setCompare(false);
      setLeftId(doc.id);
      setRightId(null);
      setDraft(null);
      setActivePinId(null);
      setUploadOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: reset compare/pin state only when the dialog opens or the document id changes, not on doc object identity changes from query refetches
  }, [open, doc?.id]);

  const { data: versions = [] } = useQuery({
    queryKey: ["doc-versions", doc?.id],
    queryFn: async () => (await api.get(`/documents/${doc.id}/versions`)).data,
    enabled: open && !!doc,
  });

  const { data: annotations = [] } = useQuery({
    queryKey: ["doc-annotations", leftId],
    queryFn: async () => (await api.get(`/documents/${leftId}/annotations`)).data,
    enabled: open && !!leftId,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["doc-annotations", leftId] });
    qc.invalidateQueries({ queryKey: ["doc-versions", doc?.id] });
    qc.invalidateQueries({ queryKey: ["documents"] });
  };

  const addMut = useMutation({
    mutationFn: async () => (await api.post(`/documents/${leftId}/annotations`, { x: draft.x, y: draft.y, text: draftText, page: 1 })).data,
    onSuccess: () => {
      setDraft(null);
      setDraftText("");
      invalidate();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const replyMut = useMutation({
    mutationFn: async (aid) => api.post(`/annotations/${aid}/reply`, { text: replyText }),
    onSuccess: () => {
      setReplyText("");
      invalidate();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const resolveMut = useMutation({
    mutationFn: async (aid) => api.put(`/annotations/${aid}/resolve`),
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(fmtErr(e)),
  });

  const uploadMut = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("file", verFile);
      fd.append("version", verLabel);
      return (await api.post(`/documents/${doc.id}/versions`, fd)).data;
    },
    onSuccess: (d) => {
      toast.success(`Revision ${d.version} uploaded`);
      setUploadOpen(false);
      setVerFile(null);
      setVerLabel("");
      invalidate();
      setLeftId(d.id);
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const leftVersion = versions.find((v) => v.id === leftId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl" data-testid="viewer-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm">{leftVersion?.filename || doc?.filename}</span>
            <Badge variant="outline" className="font-mono">{leftVersion?.version || doc?.version}</Badge>
            <Badge variant="outline" className="font-mono text-[10px]">{doc?.category}</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={compare ? "default" : "outline"}
            size="sm"
            className={compare ? "bg-slate-900" : ""}
            onClick={() => {
              const next = !compare;
              setCompare(next);
              if (next && !rightId) {
                const other = versions.find((v) => v.id !== leftId);
                setRightId(other ? other.id : leftId);
              }
            }}
            data-testid="viewer-compare-toggle"
          >
            <Columns2 className="mr-1.5 h-4 w-4" /> Compare revisions
          </Button>
          <Button variant="outline" size="sm" onClick={() => setUploadOpen(!uploadOpen)} data-testid="viewer-upload-version-button">
            <Upload className="mr-1.5 h-4 w-4" /> Upload new version
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadDocument(leftId, leftVersion?.filename || doc?.filename)} data-testid="viewer-download-button">
            <Download className="mr-1.5 h-4 w-4" /> Download
          </Button>
          {!compare && <span className="ml-auto hidden text-xs text-slate-500 md:block">Click on the drawing to pin a comment</span>}
        </div>

        {uploadOpen && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border bg-slate-50 p-3" data-testid="upload-version-form">
            <input ref={fileRef} type="file" className="hidden" onChange={(e) => setVerFile(e.target.files[0] || null)} data-testid="upload-version-file-input" />
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="upload-version-choose">
              {verFile ? verFile.name : "Choose file"}
            </Button>
            <Input value={verLabel} onChange={(e) => setVerLabel(e.target.value)} placeholder="Version (auto if empty)" className="w-44 font-mono" data-testid="upload-version-label-input" />
            <Button size="sm" onClick={() => uploadMut.mutate()} disabled={!verFile || uploadMut.isPending} data-testid="upload-version-submit">
              {uploadMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Upload revision
            </Button>
          </div>
        )}

        <div className="flex flex-col gap-4 lg:flex-row">
          <div className="min-w-0 flex-1 space-y-2">
            <VersionSelect value={leftId} versions={versions} onChange={setLeftId} testId="version-select-left" />
            <div className="overflow-auto">
              <PreviewPane
                docId={leftId}
                pins={compare ? [] : annotations}
                draft={draft}
                activePinId={activePinId}
                onImageClick={compare ? null : (p) => { setDraft(p); setActivePinId(null); }}
                onPinClick={(id) => { setActivePinId(id); setDraft(null); }}
                testPrefix="viewer-left"
              />
            </div>
          </div>

          {compare ? (
            <div className="min-w-0 flex-1 space-y-2">
              <VersionSelect value={rightId} versions={versions} onChange={setRightId} testId="version-select-right" />
              <div className="overflow-auto">
                <PreviewPane docId={rightId} pins={[]} testPrefix="viewer-right" />
              </div>
            </div>
          ) : (
            <div className="w-full shrink-0 space-y-3 lg:w-80">
              {draft && (
                <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="draft-annotation-form">
                  <div className="text-xs font-semibold text-amber-900">
                    New comment at ({draft.x.toFixed(1)}%, {draft.y.toFixed(1)}%)
                  </div>
                  <Textarea rows={2} value={draftText} onChange={(e) => setDraftText(e.target.value)} placeholder="Describe the issue…" data-testid="draft-annotation-input" />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => addMut.mutate()} disabled={!draftText.trim() || addMut.isPending} data-testid="draft-annotation-save">
                      Pin comment
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDraft(null)} data-testid="draft-annotation-cancel">
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              <div className="space-y-2" data-testid="annotations-panel">
                <div className="font-mono text-xs uppercase tracking-wider text-slate-500">Comments ({annotations.length})</div>
                {annotations.length === 0 && <p className="text-xs text-slate-400">No comments yet — click on the drawing.</p>}
                {annotations.map((a, idx) => (
                  <div
                    key={a.id}
                    className={`rounded-md border p-2.5 ${activePinId === a.id ? "border-amber-400 bg-amber-50/50" : ""} ${a.status === "resolved" ? "opacity-60" : ""}`}
                    data-testid={`annotation-card-${a.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <button className="text-left" onClick={() => setActivePinId(a.id)}>
                        <span className="font-mono text-xs font-bold text-amber-700">#{idx + 1}</span>{" "}
                        <span className="text-sm text-slate-800">{a.text}</span>
                      </button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        onClick={() => resolveMut.mutate(a.id)}
                        title={a.status === "open" ? "Resolve" : "Reopen"}
                        data-testid={`annotation-resolve-${a.id}`}
                      >
                        {a.status === "open" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Undo2 className="h-4 w-4 text-slate-500" />}
                      </Button>
                    </div>
                    <div className="mt-1 font-mono text-[10px] text-slate-500">
                      {a.created_by_name || a.created_by_email} · {fmtDateTime(a.created_at)}
                    </div>
                    {(a.replies || []).map((r) => (
                      <div key={`${r.user_email || r.user_name}-${r.created_at}`} className="mt-1.5 border-l-2 border-slate-200 pl-2 text-xs text-slate-700">
                        {r.text}
                        <div className="font-mono text-[10px] text-slate-400">
                          {r.user_name || r.user_email} · {fmtDateTime(r.created_at)}
                        </div>
                      </div>
                    ))}
                    {activePinId === a.id && (
                      <div className="mt-2 flex gap-1.5">
                        <Input value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Reply…" className="h-8 text-xs" data-testid="annotation-reply-input" />
                        <Button size="sm" className="h-8" onClick={() => replyMut.mutate(a.id)} disabled={!replyText.trim() || replyMut.isPending} data-testid="annotation-reply-submit">
                          Reply
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="space-y-1.5 border-t pt-3" data-testid="version-history">
                <div className="font-mono text-xs uppercase tracking-wider text-slate-500">Version history</div>
                {versions.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setLeftId(v.id)}
                    className={`flex w-full items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-left text-xs transition-colors hover:bg-slate-50 ${v.id === leftId ? "border-sky-400 bg-sky-50" : ""}`}
                    data-testid={`version-row-${v.id}`}
                  >
                    <span className="font-mono font-semibold">{v.version}</span>
                    <span className="truncate text-slate-600">{v.filename}</span>
                    <span className="shrink-0 text-slate-400">{fmtDateTime(v.uploaded_at)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
